import { Box, Button, Grid, styled, Typography, useTheme } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import FolderIcon from '@mui/icons-material/Folder';
import React, { DragEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ACCEPT_TYPE_IMAGE } from '../../../../_constants/inspectionConst';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

type AcceptUploadFileProps = {
  index: number;
  isImage: boolean;
  setFile: (index: number, file: File | undefined) => void;
};

const AcceptUploadFile: React.FC<AcceptUploadFileProps> = ({
  index,
  isImage,
  setFile,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const { t } = useTranslation();
  const theme = useTheme();
  const handleDragEnter = useCallback(
    (event: React.DragEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(true);
    },
    [],
  );

  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
      // relatedTargetが親要素内に存在するかをチェック
      if (!event.currentTarget.contains(event.relatedTarget as Node)) {
        setIsDragging(false);
      }
    },
    [],
  );

  const handleDrop = (index: number, event: DragEvent<HTMLSpanElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    setFile(index, file);
  };

  const onChangeFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setFile(index, undefined);
      return;
    }
    setFile(index, file);
    // アップロードするとstateの更新に関わらずinput要素のvalueはセットされる
    // 連続で同じファイルをアップロードすると
    // valueが変わらずonChangeが発火しないため都度リセットする
    event.target.value = '';
  };

  return (
    <Grid
      size={12}
      container
      spacing={2}
      sx={{
        margin: '40px 0px',
        border: '4px dashed',
        borderColor: isDragging
          ? theme.palette.primary.main
          : 'rgba(150,150,150,0.4)',
        borderRadius: '4px',
      }}
    >
      <Box
        id="drop-zone"
        onDrop={(event) => {
          handleDrop(index, event);
        }}
        onDragEnter={(event) => {
          handleDragEnter(event);
        }}
        onDragOver={(event) => {
          event.preventDefault();
        }}
        onDragLeave={(event) => {
          handleDragLeave(event);
        }}
        style={{
          padding: '20px',
          textAlign: 'center',
          width: '100%',
          backgroundColor: isDragging ? 'rgba(150,150,150,0.2)' : '',
        }}
      >
        <Grid size={12} textAlign="center">
          <CloudUploadOutlinedIcon
            style={{ fontSize: '64px', fill: 'grey.500' }}
          />
        </Grid>
        <Grid size={12} textAlign="center">
          <Typography variant="caption" component="div" gutterBottom>
            {isImage ? t('LBL_IMAGE_UPLOAD') : t('BTN_DOCUMENT_UPLOAD')}
          </Typography>
        </Grid>
        <Grid size={12} textAlign="center">
          <Typography
            variant="caption"
            component="div"
            sx={{
              visibility: isImage ? 'visible' : 'hidden',
              color: 'grey.500',
            }}
          >
            {t('LBL_RESTRICT_FILE_TYPE')}
          </Typography>
        </Grid>
        <Grid size={12} textAlign="center" py="10px">
          <Button
            component="label"
            role={undefined}
            variant="contained"
            sx={{ bgcolor: '#fff', color: '#000', textTransform: 'none' }}
            tabIndex={-1}
            endIcon={<FolderIcon color="primary" />}
          >
            {isImage ? t('BTN_SELECT_FILE') : t('BTN_DOCUMENT_UPLOAD')}
            <VisuallyHiddenInput
              type="file"
              accept={isImage ? ACCEPT_TYPE_IMAGE : undefined}
              onChange={onChangeFile}
            />
          </Button>
        </Grid>
      </Box>
    </Grid>
  );
};

export default AcceptUploadFile;
